import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, {AxiosError} from 'axios';
import _uniqBy from 'lodash/uniqBy';
import _filter from 'lodash/filter';
import { clearError } from './errorSlice';
import { handleApiError } from '@/utils/apiErrorHandler';

export const fetchAdjudication = createAsyncThunk('case/fetchAdjudication', async ({ requestID, caseID }: any, { getState, dispatch }) => {
  let routeUrl = `/papi/secure/${requestID}/requests/adjudication?caseID=${caseID}`;
  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl Fetching case details from:', routeUrl);
    const response = await axios.get(routeUrl);

    return response?.data?.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "case", dispatch);
    throw error;
  }
});

export const fetchCaseDetails = createAsyncThunk(
  'case/fetchCaseDetails',
  async ({ mpin, isDelegate }: any, { dispatch }) => {
    let routeUrl = isDelegate ? `/papi/secure/physician/${mpin}/case/details` : `/papi/secure/case/details`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching case details from:', routeUrl);
      const response = await axios.get(routeUrl);

      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "case", dispatch);
      throw error;
    }
  }
);

export const caseSubmitPost = createAsyncThunk(
  'case/caseSubmitPost', async ({ mpin, payload, isDelegate }: { mpin: string, payload: any, isDelegate: boolean }, { dispatch }) => {
    let routeUrl = isDelegate ? `/papi/secure/physician/${mpin}/case/submit` : `/papi/secure/case/submit`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl case submit post from:', routeUrl);
      const response = await axios.post(routeUrl, payload);
      return response.status;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "case", dispatch);
      throw error;
    }
  });

export const fetchCaseStatus = createAsyncThunk('case/fetchCaseStatus', 
  async ({ mpin, sort, isDelegate }: any, { dispatch }) => {
    let routeUrl = isDelegate ? `/papi/secure/physician/${mpin}/case/status?sort=${sort}` : `/papi/secure/case/status?sort=${sort}`;
  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl Fetching case details from:', routeUrl);
    const response = await axios.get(routeUrl);

    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "case", dispatch);
    throw error;
  }
});

export const fetchCaseSummary = createAsyncThunk('case/fetchCaseSummary', async ({ mpin, caseID, isDelegate }: any, { dispatch }) => {
  let routeUrl = isDelegate ? `/papi/secure/physician/${mpin}/case/summary?caseID=${caseID}` : `/papi/secure/case/summary?caseID=${caseID}`;
  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl Fetching case summary from:', routeUrl);
    const response = await axios.get(routeUrl);
    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "case", dispatch);
    throw error;
  }
});

// Create slice
const caseSlice = createSlice({
  name: 'case',
  initialState: {
    adjudication: {
      decision: '',
      informationConsidered: ''
    },
    caseDetails: { data: [], loading: false },
    caseData: { data: [], loading: false },
    caseSummary: { data: {}, loading: false, error: null },
    loading: false,
    error: null as string | null,
    selectedCaseId: null,
    reconModalData: {}
  },
  reducers: {
    setReconModalData: (state, action) => {
      state.reconModalData = action.payload;
    },
    clearReconModalData: (state) => {
      state.reconModalData = {};
    },
    clearCaseInfo: (state) => {
      state.caseData = { data: [], loading: false };
      state.selectedCaseId = null;
    },
    clearCaseCardData: (state, action) => {
      if (action.payload) {
        state.caseData = { data: [], loading: false };
        state.selectedCaseId = null;
      }
    },
    setSelectedCaseId: (state, action) => {
      state.selectedCaseId = action.payload;
    },
    clearCaseSummary: (state) => {
      state.caseSummary = { data: {}, loading: false, error: null };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdjudication.pending, (state) => {
        state.error = null;
        state.adjudication.decision = '';
        state.adjudication.informationConsidered = '';
      })
      .addCase(fetchAdjudication.fulfilled, (state, action) => {
        state.adjudication.decision = action.payload.decision;
        state.adjudication.informationConsidered = action.payload.informationConsidered;
      })
      .addCase(fetchAdjudication.rejected, (state, action) => {
        state.adjudication.decision = '';
        state.adjudication.informationConsidered = '';
        state.error = action.error.message ?? null;
      })
      .addCase(fetchCaseDetails.pending, (state) => {
        state.caseDetails.loading = true;
        state.error = null;
        state.caseDetails.data = [];
      })
      .addCase(fetchCaseDetails.fulfilled, (state, action) => {
        state.caseDetails.loading = false;
        state.caseDetails.data = action.payload.data;
      })
      .addCase(fetchCaseDetails.rejected, (state, action) => {
        state.caseDetails.data = [];
        state.caseDetails.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchCaseStatus.pending, (state) => {
        state.caseData.loading = true;
        state.error = null;
        state.caseData.data = [];
      })
      .addCase(fetchCaseStatus.fulfilled, (state, action) => {
        const casedata = action.payload.data;
        state.caseData.loading = false;
        state.caseData.data = casedata;
      })
      .addCase(fetchCaseStatus.rejected, (state, action) => {
        state.caseData.data = [];
        state.caseData.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchCaseSummary.pending, (state) => {
        state.caseSummary.loading = true;
        state.error = null;
        state.caseSummary.data = {};
      })
      .addCase(fetchCaseSummary.fulfilled, (state, action) => {
        state.caseSummary.loading = false;
        state.caseSummary.data = action.payload.data;
      })
      .addCase(fetchCaseSummary.rejected, (state, action) => {
        state.caseSummary.data = {};
        state.caseSummary.loading = false;
        state.error = action.error.message ?? null;
      })
  }
});

export const {
  clearCaseInfo,
  clearCaseCardData,
  setSelectedCaseId,
  setReconModalData,
  clearReconModalData,
  clearCaseSummary,
} = caseSlice.actions;

export default caseSlice.reducer;
